import { FSWatcher, watch, stat } from "fs";
import path from "path";

import _ from "lodash";
import { FarmConfig } from "Tools/src/CandyCLI";

import { httpGet } from "../../../Commons/HttpServer/AttributeRouting/Methods";
import { httpQueryParam, httpType } from "../../../Commons/HttpServer/AttributeRouting/Params";
import { httpReturn } from "../../../Commons/HttpServer/AttributeRouting/Results";
import { SingletonController } from "../../../Commons/HttpServer/ExpressWrapper/SingletonController";
import { WellKnownDirectories } from "../../../../../Common/WellKnownDirectories";
import { getGitBranch } from "../../../Commons/GitUtils";

import { globToRegexp } from "./GlobToRegexp";

type FSUpdateHandler = (eventType: string, filePath: string, rootDir: string, force?: boolean) => void;

interface DirectoryWatcher {
    fsWatcher: FSWatcher;
    gitPollIntervalId: NodeJS.Timeout;
}
const GIT_BRANCH_POLL_DELAY = 3000;

export class BuildServerBackendResourcesUpdatesController extends SingletonController {
    private lastUpdateTime = 0;
    private readonly updateMonitor: Monitor = new Monitor();
    private farmWatchers?: DirectoryWatcher[];
    private formWatchers?: DirectoryWatcher[];
    private readonly allFarmConfig: FarmConfig[];
    private readonly farmWatchingRegexps = [
        "sugarTemplates",
        "legacyfetchfunctions",
        "kclangfetchfunctions",
        "jsHelpers",
        `images`,
        "tour",
        "controls",
        "commonResources",
        "externalConverters",
    ].map(globToRegexp);
    private readonly formsWatchingRegexps = [
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/schemas`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/converters`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/mergers`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/autocalc`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/validations`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/fetchfunctions`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/helpers`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/${WellKnownDirectories.FormJsonFileName}`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/sugar`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/normalize`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/normalizers`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/unmap`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/map`,
        `**/**/**/${WellKnownDirectories.CandySubdirectoryName}/patch`,
    ].map(globToRegexp);

    public constructor(allFarmConfig: FarmConfig[]) {
        super();
        this.allFarmConfig = allFarmConfig;
        this.lastUpdateTime = Date.now();
    }

    public async beforeStart(): Promise<void> {
        this.farmWatchers = this.allFarmConfig.map(config =>
            this.getDirectoryWatcher(config.farmDirectory, this.getHandleFSUpdate(this.farmWatchingRegexps))
        );

        this.formWatchers = this.allFarmConfig.map(config => {
            const formRootDirectory = path.normalize(
                path.join(config.farmDirectory, "..", WellKnownDirectories.FormsDirectory)
            );

            return this.getDirectoryWatcher(formRootDirectory, this.getHandleFSUpdate(this.formsWatchingRegexps));
        });
    }

    private getDirectoryWatcher(directoryPath: string, fsUpdateHandler: FSUpdateHandler): DirectoryWatcher {
        // @ts-ignore
        const fsWatcher = watch(
            directoryPath,
            { recursive: true },
            _.debounce((x: string, y: string) => fsUpdateHandler(x, y, directoryPath), 200)
        );

        let savedBranch: string | null = null;
        const checkGitBranch = () => {
            const currentBranch = getGitBranch(directoryPath);

            if (!savedBranch) {
                savedBranch = currentBranch;
            } else if (currentBranch !== savedBranch) {
                savedBranch = currentBranch;
                fsUpdateHandler("git", "", directoryPath, true);
            }
        };

        return { fsWatcher: fsWatcher, gitPollIntervalId: setInterval(checkGitBranch, GIT_BRANCH_POLL_DELAY) };
    }

    @httpGet(
        "/updates-observer/check-last-update-long-poll",
        httpReturn.PlainText,
        httpQueryParam("prevLastUpdate", httpType.Number)
    )
    public async getLastUpdateTime(): Promise<string> {
        return this.lastUpdateTime.toString();
    }

    public async afterStop(): Promise<void> {
        const clearWatcher = (watcher: DirectoryWatcher) => {
            watcher.fsWatcher.close();
            clearInterval(watcher.gitPollIntervalId);
        };
        this.farmWatchers?.forEach(clearWatcher);
        this.formWatchers?.forEach(clearWatcher);
    }

    private readonly getHandleFSUpdate: (directoriesRegExps: RegExp[]) => FSUpdateHandler =
        (directoriesRegExps: RegExp[]) =>
        (_eventType: string, filePath: string, rootDir: string, force: boolean = false) => {
            if (force || directoriesRegExps.some(x => x.test(filePath))) {
                stat(path.join(rootDir, filePath), (_err, fileStat) => {
                    const fileTicks = Math.max(fileStat.mtimeMs, fileStat.ctimeMs);
                    if (force || fileTicks > this.lastUpdateTime) {
                        this.lastUpdateTime = Date.now();
                        this.updateMonitor.pulseAll();
                    }
                });
            }
        };
}

class Monitor {
    private awaiters: Array<() => void> = [];

    public wait(): Promise<void> {
        return new Promise(this.waitPromiseHandler);
    }

    public pulseAll(): void {
        const awaiters = [...this.awaiters];
        this.awaiters = [];
        for (const awaiter of awaiters) {
            awaiter();
        }
    }

    private readonly waitPromiseHandler = (resolve: () => void) => {
        this.awaiters.push(resolve);
    };
}
